(() => {
  const root = document.querySelector('[data-ge-address-verification]');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const find = selector => root.querySelector(selector);
  const form = find('form');
  const field = name => form.elements.namedItem(name);
  const street = field('street');
  const suggestions = find('[data-address-suggestions]');
  const confirmation = find('[data-address-confirmation]');
  const results = find('[data-address-results]');
  const attribution = find('[data-address-attribution]');
  const addressStatus = find('[data-address-status]');
  const status = message => {
    const element = find('[data-status]');
    element.textContent = message || '';
    element.hidden = !message;
  };
  const invitationToken = new URLSearchParams(location.hash.slice(1)).get('invite');
  if (invitationToken) history.replaceState(null, '', location.pathname + location.search);
  let sessionToken = crypto.randomUUID();
  let validation = null;
  let confirmed = false;
  let snapshotToken = null;
  let revision = 0;
  let timer;
  let busy = false;
  let received = false;
  let lookupAddress = '';
  // Preserve user-entered unit wording: Google's '#310' output may fail revalidation.
  let unitForValidation = '';

  function showReceipt(result) {
    received = true;
    form.hidden = true;
    find('[data-success]').hidden = false;
    status(result.message);
    find('[data-success]').focus();
  }
  async function api(action, values = {}) {
    const response = await fetch(root.dataset.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...values, action, invitationToken }),
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      signal: AbortSignal.timeout(20000),
    });
    const result = await response.json();
    const missingSubpremise = action === 'validate' && result.reason === 'missing_subpremise' && result.address;
    if ((!response.ok || !result.ok) && !missingSubpremise) throw new Error(result.message || 'We could not complete that request. Please try again.');
    if (['submitted', 'processing'].includes(result.status)) showReceipt(result);
    return result;
  }
  function updateSubmit() {
    find('[data-submit]').disabled = busy || received || !validation || !confirmed;
  }
  function setAddressStatus(message, tone = 'neutral') {
    addressStatus.textContent = message || '';
    addressStatus.classList.toggle('ge-is-hidden', !message);
    addressStatus.classList.toggle('ge-address-status--error', tone === 'error');
    addressStatus.classList.toggle('ge-address-status--success', tone === 'success');
  }
  function renderResults(open = false) {
    suggestions.classList.toggle('ge-is-hidden', !open);
    street.setAttribute('aria-expanded', String(open));
    results.hidden = !open && confirmation.classList.contains('ge-is-hidden');
    attribution.classList.toggle('ge-is-hidden', results.hidden);
  }
  function invalidate() {
    revision++;
    clearTimeout(timer);
    validation = null;
    confirmed = false;
    confirmation.classList.add('ge-is-hidden');
    suggestions.replaceChildren();
    renderResults();
    updateSubmit();
  }
  function applyAddress(a) {
    street.value = a.address || '';
    field('unit').value = a.address2 || '';
    field('city').value = a.city || '';
    field('zip').value = a.zip || '';
    field('state').value = a.state || '';
    lookupAddress = [a.address, a.city, a.state, a.zip, a.country].filter(Boolean).join(', ');
  }
  function busyState(value) {
    busy = value;
    find('[data-fields]').disabled = value;
    form.setAttribute('aria-busy', String(value));
    updateSubmit();
  }
  async function verify(selectedAddress) {
    if (busy || received || !selectedAddress) return;
    invalidate();
    busyState(true);
    setAddressStatus('Verifying address...');
    try {
      const result = await api('validate', { selectedAddress, address2: unitForValidation, sessionToken });
      if (result.reason === 'missing_subpremise' && result.address) {
        applyAddress(result.address);
        setAddressStatus(result.message, 'error');
        field('unit').focus();
        return;
      }
      if (received || !result.address || !result.validationToken) return;
      validation = result;
      if (result.status === 'confirmation_required') {
        const a = result.address;
        find('[data-address-confirmation-address]').textContent = [a.address, a.address2, a.city, a.state, a.zip].filter(Boolean).join(', ');
        confirmation.classList.remove('ge-is-hidden');
        renderResults();
        setAddressStatus('');
      } else {
        confirmed = true;
        applyAddress(result.address);
        setAddressStatus(result.message || 'Address verified.', 'success');
      }
    } catch (error) { setAddressStatus(error.message, 'error'); }
    finally { sessionToken = crypto.randomUUID(); busyState(false); }
  }
  async function fetchSuggestions() {
    if (busy || received) return;
    const input = street.value.trim();
    const current = ++revision;
    if (input.length < 3) { renderResults(); return; }
    try {
      const result = await api('autocomplete', { input, sessionToken });
      if (current !== revision || busy || received || !result.suggestions) return;
      suggestions.replaceChildren(...result.suggestions.map(item => {
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = item.text;
        button.addEventListener('click', () => { lookupAddress = item.text; void verify(item.text); });
        li.append(button);
        return li;
      }));
      renderResults(result.suggestions.length > 0);
    } catch (error) {
      if (current !== revision || busy || received) return;
      renderResults();
      setAddressStatus('Address suggestions are temporarily unavailable. Please try again.', 'error');
    }
  }
  street.addEventListener('input', () => {
    invalidate();
    lookupAddress = '';
    for (const name of ['city', 'zip', 'state']) field(name).value = '';
    setAddressStatus('');
    timer = setTimeout(fetchSuggestions, 300);
  });
  street.addEventListener('focus', () => {
    clearTimeout(timer);
    timer = setTimeout(fetchSuggestions, 0);
  });
  street.addEventListener('keydown', event => {
    if (event.key === 'ArrowDown' && suggestions.querySelector('button')) {
      event.preventDefault();
      suggestions.querySelector('button').focus();
    } else if (event.key === 'Escape') renderResults();
  });
  field('unit').addEventListener('input', () => {
    unitForValidation = field('unit').value.trim();
    invalidate();
    setAddressStatus('');
  });
  field('unit').addEventListener('change', () => {
    if (lookupAddress) void verify(lookupAddress);
  });
  find('[data-address-confirm]').addEventListener('click', () => {
    if (!validation || busy) return;
    confirmed = true;
    applyAddress(validation.address);
    confirmation.classList.add('ge-is-hidden');
    renderResults();
    setAddressStatus('Address confirmed.', 'success');
    updateSubmit();
    field('unit').focus();
  });
  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (busy || received || !validation || !confirmed) return;
    busyState(true);
    status('Submitting your verified address…');
    const feedback = find('[data-submit-feedback]');
    feedback.hidden = false;
    feedback.textContent = 'Submitting your verified address…';
    try {
      const result = await api('submit', { snapshotToken, validationToken: validation.validationToken, confirmed });
      if (!received) showReceipt(result);
    } catch (error) {
      status(error.message);
      feedback.textContent = error.message;
      feedback.focus();
    }
    finally { busyState(false); }
  });
  if (!invitationToken) {
    find('[data-status]').hidden = true;
    find('[data-missing-invitation]').hidden = false;
    return;
  }
  api('prefill').then(async result => {
    if (received || !result.address) return;
    snapshotToken = result.snapshotToken;
    unitForValidation = result.address.address2 || '';
    applyAddress(result.address);
    form.hidden = false;
    status('');
    await verify(lookupAddress);
  }).catch(error => status(error.message));
})();
